import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MaterialModule } from "../../material/material.module";
import { Organisation } from "../../common/organisation";
import { USER_ROLE } from "../../common/user";
import { ONBOARDING_CHOICE } from "../../user/onboarding";

interface HomeIntroData {
    name:string
    canAddModels:boolean
}

// what the home page shows, for the key
const HOME_KEY = [
    {icon: 'dashboard', name: 'Overview', text: 'Headline figures for the workspace: cost, workload, and what to improve first'},
    {icon: 'account_tree', name: 'Models', text: 'How each process works: its actions, information, people and systems'},
    {icon: 'construction', name: 'Projects', text: 'Changes being modelled, compared with how things are today'},
    {icon: 'groups', name: 'People and systems', text: 'The roles and systems of the organisation, shared by every model'},
]

// shown the first time someone opens the home page of an organisation that has models: what's on it, and ways to learn
// LINQ. It closes with CREATE to create a model, with one of the other choices for help, or with nothing to look around
@Component({
    selector: 'app-home-intro-dialog',
    standalone: true,
    imports: [MaterialModule],
    template: `
        <div class="header">
            <mat-icon class="header-icon" aria-hidden="true">business</mat-icon>
            <div>
                <h2 mat-dialog-title>Welcome to {{data.name}}</h2>
                <p class="subtitle">This is the organisation's home page: everything in it, at a glance.</p>
            </div>
        </div>
        <mat-dialog-content class="content">
            <ul class="key" aria-label="What the home page shows">
                @for (k of homeKey; track k.name) {
                    <li><mat-icon aria-hidden="true">{{k.icon}}</mat-icon><span><b>{{k.name}}</b>{{k.text}}</span></li>
                }
            </ul>

            <div class="workspaces">
                <mat-icon aria-hidden="true">layers</mat-icon>
                <p>Models live in <b>workspaces</b>. <b>Current state</b> is how the organisation works today. A <b>project</b> is a workspace for
                    modelling a change, and is merged into current state when the change is made for real.</p>
            </div>

            <h3>Where would you like to start?</h3>
            <div class="starts">
                @if (data.canAddModels) {
                    <button type="button" class="start" (click)="close(CREATE)" data-testid="home-intro-create">
                        <mat-icon aria-hidden="true">add_circle</mat-icon>
                        <span class="start-title">Create a model</span>
                        <span class="start-text">Model another process, with AI building a first draft or on a blank canvas.</span>
                    </button>
                }
                <button type="button" class="start" (click)="close(WALKTHROUGH)" data-testid="home-intro-walkthrough">
                    <mat-icon aria-hidden="true">school</mat-icon>
                    <span class="start-title">Learn the basics</span>
                    <span class="start-text">A short walkthrough of building a LINQ model, a step at a time.</span>
                </button>
                <button type="button" class="start" (click)="close()" data-testid="home-intro-look-around">
                    <mat-icon aria-hidden="true">explore</mat-icon>
                    <span class="start-title">Look around</span>
                    <span class="start-text">Open the models and insights already here to see what LINQ shows.</span>
                </button>
            </div>
            <p class="note"><mat-icon aria-hidden="true">help</mat-icon><span>Help guides, videos and Ask LINQ AI are in the help menu, top right, whenever you need them.</span></p>
        </mat-dialog-content>
        <mat-dialog-actions class="actions">
            <span class="help-label">More help</span>
            <button mat-button (click)="close(VIDEOS)"><mat-icon>play_circle</mat-icon>Onboarding videos</button>
            <button mat-button (click)="close(TUTORIAL)"><mat-icon>menu_book</mat-icon>Tutorials</button>
        </mat-dialog-actions>
    `,
    styles: [`
        .header { display: flex; align-items: center; gap: 14px; padding: 20px 24px 4px; }
        .header-icon { flex: none; width: 44px; height: 44px; font-size: 26px; display: grid; place-items: center; border-radius: 12px;
            color: #00706d; background: rgba(0, 169, 165, .12); }
        .header h2 { margin: 0; padding: 0; font-size: 20px; font-weight: 600; line-height: 1.3; color: var(--ink, #1e2233); overflow-wrap: anywhere; }
        .header h2::before { display: none; }
        .subtitle { margin: 2px 0 0; font-size: 14px; color: var(--slate, #5b6172); }
        .content { display: flex; flex-direction: column; gap: 14px; }
        .key { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px 20px; margin: 0; padding: 14px 16px; list-style: none;
            background: var(--bg, #f4f5f9); border-radius: 10px; }
        .key li { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; line-height: 1.45; color: var(--slate, #5b6172); }
        .key b { display: block; color: var(--ink, #1e2233); font-size: 14px; }
        .key mat-icon { flex: none; color: #00706d; }
        .workspaces { display: flex; align-items: flex-start; gap: 10px; }
        .workspaces mat-icon { flex: none; color: #00706d; }
        .workspaces p { margin: 0; line-height: 1.55; color: var(--ink, #1e2233); }
        h3 { margin: 4px 0 0; font-size: 15px; font-weight: 600; }
        .starts { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
        .start { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 16px; font: inherit; text-align: left; color: var(--ink, #1e2233);
            background: var(--paper, #fff); border: 1px solid var(--line, #e7e9f0); border-radius: 10px; cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease; }
        .start:hover, .start:focus-visible { border-color: var(--teal, #00a9a5); box-shadow: 0 6px 18px rgba(0, 169, 165, .15); }
        .start:focus-visible { outline: 2px solid var(--teal, #00a9a5); outline-offset: 2px; }
        .start mat-icon { width: 28px; height: 28px; font-size: 28px; color: #00706d; }
        .start-title { font-size: 15px; font-weight: 600; }
        .start-text { font-size: 13px; line-height: 1.5; color: var(--slate, #5b6172); }
        .note { display: flex; align-items: flex-start; gap: 8px; margin: 0; font-size: 13px; line-height: 1.5; color: var(--slate, #5b6172); }
        .note mat-icon { flex: none; width: 18px; height: 18px; font-size: 18px; color: #00706d; }
        .actions { flex-wrap: wrap; gap: 4px; }
        .help-label { margin-right: auto; padding-left: 8px; font-size: 13px; color: var(--slate, #5b6172); }
    `],
})
export class HomeIntroDialogComponent {
    readonly homeKey = HOME_KEY
    readonly CREATE = ONBOARDING_CHOICE.CREATE
    readonly VIDEOS = ONBOARDING_CHOICE.VIDEOS
    readonly WALKTHROUGH = ONBOARDING_CHOICE.WALKTHROUGH
    readonly TUTORIAL = ONBOARDING_CHOICE.TUTORIAL

    constructor(private dialogRef: MatDialogRef<HomeIntroDialogComponent, ONBOARDING_CHOICE>, @Inject(MAT_DIALOG_DATA) public data:HomeIntroData) {}

    // only someone who can add a model is offered to create one
    static open(dialog:MatDialog, organisation:Organisation) {
        const data:HomeIntroData = {name: organisation.name, canAddModels: !!organisation.role && organisation.role <= USER_ROLE.WRITE}
        return dialog.open<HomeIntroDialogComponent, HomeIntroData, ONBOARDING_CHOICE>(HomeIntroDialogComponent,
            {data, width: '760px', maxWidth: '95vw', maxHeight: '90vh', hasBackdrop: true, autoFocus: 'dialog'})
    }

    close(choice?:ONBOARDING_CHOICE) {
        this.dialogRef.close(choice)
    }
}
